import { useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ACHIEVEMENTS, BRAND_COLORS, SUBJECT_THEME, UI_ASSETS, type MeResponse } from '@lk/shared';
import { BrandImg } from '../../components/BrandImg';
import { Button } from '../../components/Button';
import { CoinCounter } from '../../components/CoinCounter';
import { Confetti } from '../../components/Confetti';
import { Icon } from '../../components/Icon';
import { Panel, Stat } from '../../components/Panel';
import { SyncIndicator } from '../../components/SyncIndicator';
import { LoadingState } from '../../components/States';
import { answersLabel } from '../../lib/format';
import { humanMessageForCode } from '../../lib/errors';
import { getDb } from '../../offline/db';
import { attemptsForSession } from '../../offline/queue';
import { loadSession } from '../../offline/sessions';
import { useLiveQuery } from '../../offline/useLiveQuery';
import { useSyncState } from '../../offline/useSync';
import { getSyncer } from '../../offline/runtime';
import { playSound } from '../../audio/sound';
import { describeResponse } from '../quiz/draft';
import type { QuizItem } from '../quiz/types';
import { QuestionText } from '../../components/QuestionText';

export function SummaryScreen({
  sessionId,
  me,
}: {
  sessionId: string;
  me: MeResponse;
}): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sync = useSyncState();
  const learnerId = me.user.learnerId ?? me.learners[0]?.id ?? '';

  const stored = useLiveQuery(
    async () => (await loadSession(sessionId)) ?? null,
    [sessionId],
    undefined as Awaited<ReturnType<typeof loadSession>> | null | undefined,
  );
  const attempts = useLiveQuery(
    () => attemptsForSession(getDb(), sessionId),
    [sessionId],
    [] as Awaited<ReturnType<typeof attemptsForSession>>,
  );

  // Свіжий гаманець після сесії — авторитетний, із сервера.
  useEffect(() => {
    if (!learnerId) return;
    void queryClient.invalidateQueries({ queryKey: ['wallet', learnerId] });
    void queryClient.invalidateQueries({ queryKey: ['progress', learnerId] });
  }, [learnerId, queryClient, sync.lastSyncAt]);

  useEffect(() => {
    playSound('sessionEnd');
  }, []);

  const itemsById = useMemo(() => {
    const map = new Map<string, QuizItem>();
    for (const item of stored?.payload.items ?? []) map.set(item.id, item);
    return map;
  }, [stored]);

  const graded = attempts.filter((row) => row.result !== null);
  /*
   * «Ще не перевірено» і «не зараховано» — РІЗНІ речі, і зливати їх не можна.
   *
   * Доки підсумок рахував усе без результату як «awaiting», під рядками, за
   * які балів не буде ніколи, стояло «Щойно зʼявиться інтернет — результат і
   * бали додадуться сюди автоматично. Нічого не втрачено». Дитина чекала
   * монет, які не прийдуть.
   */
  const refused = attempts.filter((row) => row.result === null && row.status === 'rejected');
  const awaiting = attempts.filter((row) => row.result === null && row.status !== 'rejected');
  const refusalReasons = [
    ...new Set(
      refused.map((row) =>
        row.rejection
          ? humanMessageForCode(row.rejection.code, row.rejection.message)
          : (row.lastError ?? 'Причину сервер не назвав'),
      ),
    ),
  ];
  const correct = graded.filter((row) => row.result?.isCorrect).length;
  const coins = graded.reduce((sum, row) => sum + (row.result?.coinsAwarded ?? 0), 0);
  const xp = graded.reduce((sum, row) => sum + (row.result?.xpAwarded ?? 0), 0);
  const achievements = [
    ...new Set(graded.flatMap((row) => row.result?.unlockedAchievements ?? [])),
  ];
  const mistakes = graded.filter((row) => row.result && !row.result.isCorrect);

  const flawless =
    graded.length > 0 && mistakes.length === 0 && awaiting.length === 0 && refused.length === 0;

  if (stored === undefined) return <LoadingState label="Збираємо підсумок…" />;

  return (
    <main className="lk-shell lk-safe flex min-h-dvh flex-col gap-4 pt-4 pb-8">
      {flawless ? <Confetti /> : null}

      <header className="text-center">
        <BrandImg
          src={flawless ? UI_ASSETS.trophy.path : UI_ASSETS.coinStack.path}
          alt="Підсумок сесії"
          fallbackIcon={flawless ? 'trophy' : 'coin'}
          colors={[BRAND_COLORS.gold, BRAND_COLORS.goldDeep]}
          shape="rounded"
          className="lk-anim-pop mx-auto h-28 w-28"
          eager
        />
        <h1 className="lk-title-gold mt-3 text-3xl">
          {flawless
            ? 'Бездоганно!'
            : awaiting.length > 0 || refused.length > 0
              ? 'Сесію завершено'
              : 'Гарна робота!'}
        </h1>
        <p className="mt-1 text-sm font-bold text-white/60">
          {graded.length > 0
            ? `${correct} з ${graded.length} правильно`
            : 'Результати зʼявляться після синхронізації'}
        </p>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <Stat
          label="Зароблено"
          value={<CoinCounter value={coins} withSound suffix={<span className="text-sm">грн</span>} />}
          tone={BRAND_COLORS.gold}
          icon={<Icon name="coin" size={14} />}
        />
        <Stat
          label="XP"
          value={xp}
          tone={BRAND_COLORS.cyan}
          icon={<Icon name="energy" size={14} />}
        />
        <Stat
          label="Влучність"
          value={graded.length > 0 ? `${Math.round((correct / graded.length) * 100)}%` : '—'}
          tone={BRAND_COLORS.green}
          icon={<Icon name="target" size={14} />}
        />
      </div>

      {refused.length > 0 ? (
        <Panel tight className="border-[var(--lk-red)]/60 p-4">
          <p className="text-sm font-black text-[var(--lk-red)]">
            {answersLabel(refused.length)} не зараховано
          </p>
          {/* Причини згруповані: пачка на двадцять пʼять не має обертатися
              стіною однакових червоних рядків. */}
          <ul className="mt-1 flex flex-col gap-0.5">
            {refusalReasons.map((reason) => (
              <li key={reason} className="text-xs font-semibold text-white/60">
                {reason}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs font-semibold text-white/45">
            Балів за них не буде. Ми їх не видалили — батьки можуть спробувати надіслати ще раз.
          </p>
        </Panel>
      ) : null}

      {awaiting.length > 0 ? (
        <Panel tight className="border-[var(--lk-cyan)]/60 p-4">
          <p className="text-sm font-black">
            {answersLabel(awaiting.length)} ще не перевірено
          </p>
          <p className="mt-1 text-xs font-semibold text-white/60">
            Вони збережені на пристрої. Щойно зʼявиться інтернет — результат і бали додадуться сюди
            автоматично. Нічого не втрачено.
          </p>
          <Button
            tone="cyan"
            block
            className="mt-3"
            onClick={() => void getSyncer().flush()}
          >
            Спробувати надіслати зараз
          </Button>
        </Panel>
      ) : null}

      {achievements.length > 0 ? (
        <Panel className="border-[var(--lk-gold)]/60 p-4">
          <p className="text-[11px] font-black tracking-widest text-white/45 uppercase">
            Нові досягнення
          </p>
          <ul className="mt-2 flex flex-col gap-2">
            {achievements.map((id) => {
              const meta = ACHIEVEMENTS.find((a) => a.id === id);
              return (
                <li key={id} className="flex items-center gap-3">
                  <BrandImg
                    src={meta?.asset ?? `/brand/achievements/ach-${id}.png`}
                    alt={meta?.title ?? id}
                    fallbackIcon="medal"
                    colors={[BRAND_COLORS.gold, BRAND_COLORS.goldDeep]}
                    shape="circle"
                    className="h-12 w-12 shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-black">{meta?.title ?? id}</p>
                    <p className="text-xs font-semibold text-white/55">{meta?.description ?? ''}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </Panel>
      ) : null}

      {mistakes.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="px-1 text-[11px] font-black tracking-widest text-white/45 uppercase">
            Розбір помилок
          </h2>
          {mistakes.map((row) => {
            const item = itemsById.get(row.itemId);
            const theme = item ? SUBJECT_THEME[item.subject] : null;
            return (
              <Panel
                key={row.id}
                tight
                className="p-4"
                style={theme ? { borderColor: `${theme.color}66` } : undefined}
              >
                <p className="text-sm font-black">
                  <QuestionText text={item?.prompt ?? row.itemId} />
                </p>
                <p className="mt-1.5 text-xs font-bold text-[var(--lk-red)]">
                  ти відповів: {item ? describeResponse(row.payload.response, item) : '—'}
                </p>
                <p className="text-xs font-bold text-[var(--lk-green)]">
                  правильно: {row.result?.correctAnswer}
                </p>
                {row.result?.explanation ? (
                  <p className="mt-2 text-xs leading-relaxed font-semibold text-white/75">
                    {row.result.explanation}
                  </p>
                ) : null}
              </Panel>
            );
          })}
        </section>
      ) : null}

      <SyncIndicator variant="full" />

      <div className="flex flex-col gap-2">
        <Button
          tone="gold"
          size="xl"
          block
          onClick={() => void navigate({ to: '/modes' })}
          data-testid="play-again"
        >
          <Icon name="sync" size={26} />
          Ще раз
        </Button>
        <Button tone="ghost" block onClick={() => void navigate({ to: '/arena' })}>
          На арену
        </Button>
      </div>
    </main>
  );
}
